import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  CircularProgress,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { endpointLink } from '../../index';
import { useApiAccessToken } from '../../hooks/useApiAccessToken';

// ---------------------------------------------------------------------------
// Types & helpers
// ---------------------------------------------------------------------------

type EditableField = 'Type' | 'Class' | 'Group' | 'Display' | 'Location';

type RawSensorRow = {
  deviceEUI: string;
  deviceID: string | null;
  Type: string | null;
  Class: string | null;
  Group: string | null;
  Display: string | null;
  Location: string | null;
};

type DisplayRow = {
  deviceEUI: string;
  deviceID: string;
  Type: string;
  Class: string;
  Group: string;
  Display: string;
  Location: string;
};

type SortKey = keyof DisplayRow;
type SortDirection = 'asc' | 'desc';

const EDITABLE: EditableField[] = ['Type', 'Group', 'Class', 'Display', 'Location'];

const COLUMN_TOOLTIPS: Record<keyof DisplayRow, string> = {
  deviceEUI:
    'Globally unique hardware identifier (16 hex chars). The primary key in SensorInfo; matches the device on TTN.',
  deviceID:
    'Human-readable identifier set during TTN registration (e.g. "acc-ezti-04"). Shown on cards when Location is blank.',
  Type:
    'What type of sensor it is. Must be one of: Weather, RainGauge, RiverHeight, Tank, Kiln, SoilMt, FridgeTemp. The frontend uses this to pick the renderer — a wrong or missing Type means no gauge and the value reads as 0.',
  Group:
    'Which page the sensor will show up on. The dashboard query matches with LIKE so e.g. "hvac" puts the card on the HVAC page, "weather" on Weather, etc.',
  Class:
    'A smaller subgrouping within the page — e.g. "fridge" vs "freezer" for HVAC sensors. Shown as a label under the card.',
  Display:
    'Which values from this sensor we want to display on its card (e.g. TempF, Humidity, WindAvg). Comma-separated here; stored as a JSON array. The first entry also drives the card line color.',
  Location:
    'Friendly label shown under the card (e.g. "Walk-in freezer"). Leave blank to fall back to the deviceID. Use "---" to explicitly show nothing.',
};

// Class is stored as a JSON-encoded string (e.g. "\"Hvac\""). Display is a
// JSON-encoded array (e.g. "[\"TempF\",\"Humidity\"]"). Show the parsed,
// human-friendly form to the admin and re-encode on save.
const decodeClass = (raw: string | null | undefined): string => {
  if (raw == null) return '';
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed)) return parsed.join(', ');
    return String(parsed);
  } catch {
    return raw;
  }
};

const encodeClass = (value: string): string => JSON.stringify(value);

const decodeGroup = (raw: string | null | undefined): string => decodeClass(raw);

const encodeGroup = (value: string): string => {
  const items = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return JSON.stringify(items.length <= 1 ? items[0] ?? '' : items);
};

const decodeDisplay = (raw: string | null | undefined): string => {
  if (raw == null) return '';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.join(', ');
    if (typeof parsed === 'string') return parsed;
    return String(parsed);
  } catch {
    return raw;
  }
};

const encodeDisplay = (value: string): string => {
  const items = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return JSON.stringify(items);
};

// Encoders per editable field. deviceEUI / deviceID are immutable.
const ENCODERS: Record<EditableField, (value: string) => string> = {
  Type: (v) => v.trim(),
  Class: (v) => encodeClass(v.trim()),
  Group: (v) => encodeGroup(v),
  Display: (v) => encodeDisplay(v),
  Location: (v) => v.trim(),
};

const rawToDisplay = (r: RawSensorRow): DisplayRow => ({
  deviceEUI: r.deviceEUI ?? '',
  deviceID: r.deviceID ?? '',
  Type: (r.Type ?? '').trim(),
  Class: decodeClass(r.Class),
  Group: decodeGroup(r.Group),
  Display: decodeDisplay(r.Display),
  Location: r.Location ?? '',
});

const sortRows = (rows: DisplayRow[], key: SortKey, dir: SortDirection): DisplayRow[] => {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a[key] ?? '').toString().toLowerCase();
    const bv = (b[key] ?? '').toString().toLowerCase();
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const COLUMNS: { key: keyof DisplayRow; label: string; editable: boolean; width?: number | string }[] = [
  { key: 'deviceEUI', label: 'deviceEUI', editable: false, width: 200 },
  { key: 'deviceID', label: 'deviceID', editable: false, width: 160 },
  { key: 'Type', label: 'Type', editable: true, width: 140 },
  { key: 'Group', label: 'Group', editable: true, width: 140 },
  { key: 'Class', label: 'Class', editable: true, width: 140 },
  { key: 'Display', label: 'Display', editable: true, width: 300 },
  { key: 'Location', label: 'Location', editable: true, width: 200 },
];

const SensorsAdminGrid: React.FC = () => {
  const location = useLocation();
  const getApiAccessToken = useApiAccessToken({ returnTo: location.pathname });
  const queryClient = useQueryClient();

  const [sortKey, setSortKey] = useState<SortKey>('deviceID');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Selected deviceEUIs for bulk edit
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Local edit overlay: deviceEUI -> partial field changes. Only fields the
  // admin has actually touched live here; everything else is read from server.
  const [edits, setEdits] = useState<Record<string, Partial<Record<EditableField, string>>>>({});

  // Bulk-edit dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkValues, setBulkValues] = useState<Record<EditableField, string>>({
    Type: '', Class: '', Group: '', Display: '', Location: '',
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // -------- Data fetch --------
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<DisplayRow[]>({
    queryKey: ['sensorsAdmin'],
    queryFn: async () => {
      const token = await getApiAccessToken();
      const response = await fetch(`${endpointLink}sensors/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed to load sensors (${response.status})`);
      }
      const rows = (await response.json()) as RawSensorRow[];
      return rows.map(rawToDisplay);
    },
    staleTime: 60_000,
  });

  const rows = useMemo(() => data ?? [], [data]);

  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Effective value (server value overlaid with local edit) for a given cell.
  const cellValue = (row: DisplayRow, field: EditableField): string => {
    const override = edits[row.deviceEUI]?.[field];
    return override !== undefined ? override : row[field];
  };

  const isDirty = (row: DisplayRow, field: EditableField): boolean => {
    const override = edits[row.deviceEUI]?.[field];
    return override !== undefined && override !== row[field];
  };

  const setCell = (deviceEUI: string, field: EditableField, value: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      const row = { ...(next[deviceEUI] || {}) };
      row[field] = value;
      next[deviceEUI] = row;
      return next;
    });
  };

  const clearDeviceEdits = (deviceEUI: string) => {
    setEdits((prev) => {
      if (!prev[deviceEUI]) return prev;
      const next = { ...prev };
      delete next[deviceEUI];
      return next;
    });
  };

  // -------- Selection --------
  const allOnPageEUIs = useMemo(() => sortedRows.map((r) => r.deviceEUI), [sortedRows]);
  const allSelected = allOnPageEUIs.length > 0 && allOnPageEUIs.every((e) => selected.has(e));
  const someSelected = allOnPageEUIs.some((e) => selected.has(e)) && !allSelected;

  const toggleRow = (deviceEUI: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deviceEUI)) next.delete(deviceEUI);
      else next.add(deviceEUI);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allOnPageEUIs));
    }
  };

  // -------- Save mutation --------
  type SavePayload = { updates: Array<{ deviceEUI: string } & Partial<Record<EditableField, string>>> };
  const saveMutation = useMutation({
    mutationFn: async (payload: SavePayload) => {
      const token = await getApiAccessToken();
      const response = await fetch(`${endpointLink}sensors/admin`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const text = await response.text();
      if (!response.ok) {
        throw new Error(text || `Save failed (${response.status})`);
      }
      return text ? JSON.parse(text) : {};
    },
    onSuccess: (result: any) => {
      const updated = typeof result?.updated === 'number' ? result.updated : 0;
      setSuccessMessage(
        updated > 0
          ? `Saved ${updated} sensor${updated === 1 ? '' : 's'}.`
          : 'No rows changed.',
      );
      setEdits({});
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['sensorsAdmin'] });
    },
    onError: (err: unknown) => {
      setErrorMessage(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  // Build the wire payload from `edits`, encoding each field appropriately.
  const buildPayload = (): SavePayload => {
    const updates: SavePayload['updates'] = [];
    for (const deviceEUI of Object.keys(edits)) {
      const change = edits[deviceEUI];
      if (!change) continue;
      const row = rows.find((r) => r.deviceEUI === deviceEUI);
      if (!row) continue;

      const entry: { deviceEUI: string } & Partial<Record<EditableField, string>> = { deviceEUI };
      let touched = false;
      for (const field of EDITABLE) {
        const next = change[field];
        if (next === undefined) continue;
        if (next === row[field]) continue;
        entry[field] = ENCODERS[field](next);
        touched = true;
      }
      if (touched) updates.push(entry);
    }
    return { updates };
  };

  const dirtyCount = useMemo(() => buildPayload().updates.length, [edits, rows]);

  const handleSave = () => {
    setErrorMessage('');
    const payload = buildPayload();
    if (payload.updates.length === 0) {
      setErrorMessage('No changes to save.');
      return;
    }
    saveMutation.mutate(payload);
  };

  // -------- Bulk dialog --------
  // Empty fields in the bulk form are treated as "leave alone". To explicitly
  // clear a field on many rows, the admin can edit those rows inline.
  const openBulk = () => {
    setBulkValues({ Type: '', Class: '', Group: '', Display: '', Location: '' });
    setBulkOpen(true);
  };

  const filledBulkFields = useMemo(
    () => EDITABLE.filter((f) => bulkValues[f].length > 0),
    [bulkValues],
  );

  const applyBulk = () => {
    if (filledBulkFields.length === 0 || selected.size === 0) {
      setBulkOpen(false);
      return;
    }
    setEdits((prev) => {
      const next = { ...prev };
      for (const eui of Array.from(selected)) {
        const row = rows.find((r) => r.deviceEUI === eui);
        const merged: Partial<Record<EditableField, string>> = { ...(next[eui] || {}) };
        for (const f of filledBulkFields) {
          const value = bulkValues[f];
          // Skip no-op writes so dirty count stays accurate.
          if (row && row[f] === value) {
            if (merged[f] !== undefined) delete merged[f];
            continue;
          }
          merged[f] = value;
        }
        if (Object.keys(merged).length === 0) {
          delete next[eui];
        } else {
          next[eui] = merged;
        }
      }
      return next;
    });
    setBulkOpen(false);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Manage Sensors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit metadata for every device in <code>SensorInfo</code>. Click a header to sort and hover it for
              what the column does. Type into a cell to change one row. To change the same field on several rows,
              tick their checkboxes and then click <strong>Edit selected fields…</strong> in the bar that appears
              above the table. Edits stay local until you press <strong>Save changes</strong>.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isFetching ? <CircularProgress size={20} /> : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={() => {
                setEdits({});
                refetch();
              }}
              disabled={saveMutation.isPending}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={dirtyCount === 0 || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : `Save changes${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
          </Stack>
        </Stack>

        {isError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load sensors.'}
          </Alert>
        ) : null}

        {/* Contextual selection toolbar — visible only when rows are checked.
            This is where bulk edits start, mirroring the standard MUI selection
            toolbar pattern so admins can find it without hunting. */}
        {selected.size > 0 ? (
          <Toolbar
            disableGutters
            sx={(theme) => ({
              mb: 1,
              px: 2,
              borderRadius: 1,
              minHeight: 52,
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
            })}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selected.size} selected
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={openBulk}
                disabled={saveMutation.isPending}
              >
                Edit selected fields…
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelected(new Set())}
                disabled={saveMutation.isPending}
              >
                Clear selection
              </Button>
            </Stack>
          </Toolbar>
        ) : null}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'Select all rows' }}
                  />
                </TableCell>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      width: col.width,
                      whiteSpace: 'nowrap',
                      backgroundColor: 'background.paper',
                    }}
                    sortDirection={sortKey === col.key ? sortDir : false}
                  >
                    <Tooltip title={COLUMN_TOOLTIPS[col.key]} arrow placement="top">
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell sx={{ backgroundColor: 'background.paper' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 2} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 2} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No sensors found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => {
                  const rowSelected = selected.has(row.deviceEUI);
                  const rowDirty = Boolean(edits[row.deviceEUI]);
                  return (
                    <TableRow
                      key={row.deviceEUI}
                      hover
                      selected={rowSelected}
                      sx={rowDirty ? { backgroundColor: 'action.hover' } : undefined}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={rowSelected}
                          onChange={() => toggleRow(row.deviceEUI)}
                          inputProps={{ 'aria-label': `Select ${row.deviceID || row.deviceEUI}` }}
                        />
                      </TableCell>

                      {COLUMNS.map((col) => {
                        if (!col.editable) {
                          return (
                            <TableCell
                              key={col.key}
                              sx={{ fontFamily: col.key === 'deviceEUI' ? 'monospace' : undefined, whiteSpace: 'nowrap' }}
                            >
                              {row[col.key] || '—'}
                            </TableCell>
                          );
                        }
                        const field = col.key as EditableField;
                        const value = cellValue(row, field);
                        const dirty = isDirty(row, field);
                        // Display can hold long comma-separated lists
                        // (e.g. WindAvg, WindMax, WindDir, BaroPress) — let
                        // it wrap to a second line instead of clipping.
                        const allowWrap = field === 'Display';
                        return (
                          <TableCell
                            key={col.key}
                            sx={{ width: col.width, py: 0.25, verticalAlign: 'middle' }}
                          >
                            <TextField
                              value={value}
                              onChange={(e) => setCell(row.deviceEUI, field, e.target.value)}
                              size="small"
                              variant="standard"
                              fullWidth
                              multiline={allowWrap}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  ...(allowWrap
                                    ? {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                      }
                                    : {}),
                                  ...(dirty
                                    ? { backgroundColor: 'warning.light' }
                                    : {
                                        '&:hover': { backgroundColor: 'action.hover' },
                                      }),
                                },
                              }}
                              placeholder={col.label}
                            />
                          </TableCell>
                        );
                      })}

                      <TableCell padding="checkbox" sx={{ pr: 1 }}>
                        {rowDirty ? (
                          <Tooltip title="Discard edits for this row" arrow>
                            <IconButton
                              size="small"
                              onClick={() => clearDeviceEdits(row.deviceEUI)}
                              aria-label="Discard row edits"
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Bulk-edit dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit {selected.size} selected sensor{selected.size === 1 ? '' : 's'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Type into the fields you want to overwrite. Anything left blank is left alone.
            Changes stage locally — press <strong>Save changes</strong> on the page to commit.
          </Typography>
          <Stack spacing={2}>
            {EDITABLE.map((field) => (
              <Tooltip key={field} title={COLUMN_TOOLTIPS[field]} arrow placement="top">
                <TextField
                  label={field}
                  value={bulkValues[field]}
                  onChange={(e) =>
                    setBulkValues((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  size="small"
                  fullWidth
                  placeholder={
                    field === 'Display'
                      ? 'TempF, Humidity'
                      : field === 'Group'
                        ? 'hvac'
                        : field === 'Type'
                          ? 'FridgeTemp'
                          : ''
                  }
                />
              </Tooltip>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyBulk}
            disabled={filledBulkFields.length === 0 || selected.size === 0}
          >
            Apply{filledBulkFields.length > 0
              ? ` ${filledBulkFields.length} field${filledBulkFields.length === 1 ? '' : 's'}`
              : ''} to {selected.size} row{selected.size === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SensorsAdminGrid;
